/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';

// Helpers.
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';

import { trackLink, trackPage } from '../../lib/analytics-helper';

// Constants
import { POPULAR_ARTICLE } from '../constants/PropTypesShapes';
import { getBNATitle } from '../../lib/messageHelper';

// Actions.
import updateBnaData from '../actions/action-update-bna-data';

// Containers
import InteractiveGuide from './InteractiveGuideContainer';
import { articleChannelTypes, CC3Channel } from '../../common/commonConstants';
import NNAWidget from '../../common/NNAWidget';
import { getNNAWidgetTitle, redirectNnaToICFUrl } from '../../lib/nnaHelper';
import { icfArticleUpdateContext, updateContext } from '../../contactUs/actions/action-article';
import { guideClicked } from '../actions/action-icf-page';
import { filterRecommendedChannels } from '../../lib/globalHelper';

const BNAChannel = 'BNA';

const PopularArticles = (props) => {
  const {
    articles, error, updateBnaData: updateBna, isNetNewAccount,
  } = props;
  const popular = {
    common: i18n.message('smarthelp/home.home.common'),
    recommended: i18n.message('smarthelp/home.home.recommended'),
    errorMessage: i18n.message('smarthelp/Error.error.popular'),
    recommendedTile: i18n.message('smarthelp/contactOption.recommend'),
    nnaSubTitle: i18n.message('smarthelp/home.home.NNA.welcomeSetupGuideMessage'),
    getStarted: i18n.message('smarthelp/home.home.getStarted'),
  };

  const goToArticlePage = (currentArticle) => {
    let link = `smarthelp-common-questions|${currentArticle.articleId}`;
    if (currentArticle.channel === BNAChannel) {
      updateBna(currentArticle);
      link = `${link}|${BNAChannel}`;
      // triggering update context API for BNA Articles
      updateContext(currentArticle, '', true);
    }

    // if article Channel is OMNI triggering update context
    if (currentArticle.channel === articleChannelTypes.OMNI_CHANNEL) {
      // Appending FPTI link with OMNI
      link = `${link}|${articleChannelTypes.OMNI_CHANNEL}`;
      updateContext(currentArticle, '', true);
    }

    trackLink({ id: 'homePage', link });
  };

  const handleKeyPress = (event, article) => {
    if (event.which === 13) {
      goToArticlePage(article);
    }
  };

  const renderPromptICF = (c3ListItems, listItems, omniListItems) => (
    <div>
      {c3ListItems ? (
        <div className="merchant-questions">
          <ul>{c3ListItems}</ul>
        </div>
      ) : <div />}
      <InteractiveGuide icfPosition="top" />
      {omniListItems ? (
        <div className="merchant-questions">
          <ul>{omniListItems}</ul>
        </div>
      ) : <div />}
      {listItems ? (
        <div className="popular-questions">
          <ul>{listItems}</ul>
        </div>
      ) : <div />}
    </div>
  );

  // Common function to get parsed article item.
  const getParsedArticleListItem = (article) => (
    <li key={article.articleId} className="popular-title-list">
      <a
        className="popular-item"
        href={Page.getFullArticleURL(article)}
        onClick={() => goToArticlePage(article)}
        onKeyPress={(event) => handleKeyPress(event, article)}
      >
        {article.articleTitle}
      </a>
    </li>
  );

  /**
   * Render BNA articles section only if BNA articles exists.
   * Added C3 Articles at the top
   *
   * @param c3Articles
   * @param bnaArticles
   * @returns - BNA Section HTML code
   */
  const renderBnaSection = (c3Articles, bnaArticles, omniArticles) => {
    const isC3ArticlesExist = Array.isArray(c3Articles) && c3Articles.length;
    const isBnaArticlesExist = Array.isArray(bnaArticles) && bnaArticles.length;
    const isOmniArticlesExist = Array.isArray(omniArticles) && omniArticles.length;

    // Only if articles with channel CC3 or channel BNA Exist
    if (isC3ArticlesExist || isBnaArticlesExist || isOmniArticlesExist) {
      const c3ListItems = isC3ArticlesExist ? c3Articles.map((article) => getParsedArticleListItem(article)) : null;
      const bnaListItems = isBnaArticlesExist ? bnaArticles.map((article) => getParsedArticleListItem(article)) : null;
      const omniListItems = isOmniArticlesExist ? omniArticles.map((article) => getParsedArticleListItem(article)) : null;
      return (
        <div>
          <div className="pcc-user-recommended-title">
            <h1 className="popular-title">{getBNATitle()}</h1>
            <span className="pp-vx-sh-tag pp-vx-sh-tag--success">{popular.recommendedTile}</span>
          </div>
          {renderPromptICF(c3ListItems, bnaListItems, omniListItems)}
        </div>
      );
    }
    return null;
  };

  /**
   * Render FAQ section
   *
   * @param faqArticles
   * @returns - FAQ section HTML code
   */
  const renderFaqSection = (faqArticles) => {
    if (Array.isArray(faqArticles) && faqArticles.length) {
      const listItems = faqArticles.map((article) => getParsedArticleListItem(article));
      return (
        <div>
          <h1 className="popular-title">{popular.common}</h1>
          <div className="popular-questions">
            <ul>{listItems}</ul>
          </div>
        </div>
      );
    }
    return null;
  };

  // ------------------- START OF NNA [NET NEW ACCOUNT] -------------------------------

  // Get Started button on click handler.
  // Fire Click event and redirect to ICF URL.
  const nnaWidgetButtonClickHandler = () => {
    // FPTI
    trackLink({ id: 'nnaHomePage', link: 'smarthelp-home-nna-widget|button-on-click' });

    // dispatch this function to set breadcrumb
    props.guideClicked(true);

    // Update context API
    icfArticleUpdateContext(null, articles, true);

    // Redirect to ICF page
    return redirectNnaToICFUrl();
  };

  // on widget mount fire impression event.
  const onNNAWidgetMountHandler = () => trackPage({ id: 'nnaHomePage', link: 'smarthelp-home-nna-widget' });

  // Render NNA widget
  const renderNNAWidget = () => {
    if (isNetNewAccount) {
      try {
        const widgetTitle = getNNAWidgetTitle();
        return (
          <NNAWidget
            title={widgetTitle}
            subTitle={popular.nnaSubTitle}
            buttonText={popular.getStarted}
            onClickHandler={nnaWidgetButtonClickHandler}
            onComponentMountCallBack={onNNAWidgetMountHandler}
          />
        );
      } catch (e) {
        trackPage({ id: 'nnaHomePageError', link: `smarthelp-home-nna-widget-failed-to-load|${JSON.stringify(e)}` });
        return null;
      }
    }
    return null;
  };

  // Render articles section below NNA widget.
  // Al the ICF and articles will be displayed in one section.
  const renderNNARecommendationSection = () => {
    const listItems = articles.map((article) => getParsedArticleListItem(article));
    return (
      <div>
        <h1 className="popular-title">{popular.recommended}</h1>
        <div className="popular-questions">
          <InteractiveGuide icfPosition="top" />
          <ul>{listItems}</ul>
        </div>
      </div>
    );
  };

  // -------------------- END OF NNA [NET NEW ACCOUNT] --------------------------------

  const renderContents = () => {
    if (error) {
      return <div className="popular-error">{popular.errorMessage}</div>;
    }

    Page.setCanonicalTag({ name: 'home' });

    if (Array.isArray(articles) && articles.length > 0) {
      // IF NNA is enabled render all the articles in one section
      if (isNetNewAccount) {
        return (
          <div>
            {renderNNARecommendationSection()}
          </div>
        );
      }

      // Fallback
      const BnaArticles = articles.filter((article) => article.channel === BNAChannel);
      const omniArticles = filterRecommendedChannels(articles, articleChannelTypes.OMNI_CHANNEL);
      const faqArticles = articles.filter((article) => article.channel !== BNAChannel && article.channel !== CC3Channel && article.channel !== articleChannelTypes.OMNI_CHANNEL);
      const c3Articles = articles.filter((article) => article.channel === CC3Channel);

      if (Page.isLoggedIn()) {
        return (
          <div>
            {renderBnaSection(c3Articles, BnaArticles, omniArticles)}
            {renderFaqSection(faqArticles)}
          </div>
        );
      }
      return renderFaqSection(articles);
    }
    return null;
  };

  return (
    <Fragment>
      {renderNNAWidget()}
      <div className="popular-articles popular-articles__grid">
        {renderContents()}
      </div>
    </Fragment>
  );
};

function mapStateToProps(state) {
  return {
    articles: state.popular.articles,
    error: state.popular.error,
    isNetNewAccount: state.popular.isNetNewAccount,
  };
}

PopularArticles.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.shape(POPULAR_ARTICLE)),
  error: PropTypes.bool,

  // Method Prop Types.
  updateBnaData: PropTypes.func.isRequired,
  isNetNewAccount: PropTypes.bool.isRequired,
  guideClicked: PropTypes.func.isRequired,
};

PopularArticles.defaultProps = {
  articles: [],
  error: false,
  isNetNewAccount: false,
};

// Dispatch the Action as Props.
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ updateBnaData, guideClicked }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PopularArticles);
